import { useRef } from 'react'
import { Canvas, useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { Ember } from './Ember'
import type { CharacterShape } from '@/game/characters'

interface HomeEmberSceneProps {
  color: string
  emissive?: string
  reducedMotion?: boolean
  shape?: CharacterShape
  equippedProps?: string[]
  homeLight?: number
  emberEnergy?: number
  characterId?: string
}

function GazeRig({ children, reducedMotion }: { children: React.ReactNode; reducedMotion: boolean }) {
  const ref = useRef<THREE.Group>(null)
  useFrame((state) => {
    const g = ref.current
    if (!g) return
    if (reducedMotion) {
      g.rotation.set(0, 0, 0)
      return
    }
    const p = state.pointer
    const t = state.clock.elapsedTime
    const wander = Math.sin(t * 0.4) * 0.12
    const targetY = p.x * 0.5 + wander
    const targetX = -p.y * 0.28
    g.rotation.y += (targetY - g.rotation.y) * 0.06
    g.rotation.x += (targetX - g.rotation.x) * 0.06
  })
  return <group ref={ref}>{children}</group>
}

export function HomeEmberScene({ color, emissive, reducedMotion = false, shape, equippedProps, homeLight = 1, emberEnergy = 1, characterId = 'default_hero' }: HomeEmberSceneProps) {
  const light = Math.max(0.45, Math.min(1, homeLight))
  return (
    <Canvas
      dpr={[1, 1.75]}
      camera={{ position: [0, 0.35, 2.9], fov: 42 }}
      gl={{ antialias: true, alpha: true, powerPreference: 'high-performance' }}
      frameloop={reducedMotion ? 'demand' : 'always'}
    >
      <ambientLight intensity={0.55} color="#fff1dc" />
      <directionalLight position={[2.5, 3.5, 2.5]} intensity={1.15} color="#fff1dc" />
      <pointLight position={[-2.4, 0.6, -1.5]} intensity={0.7 * light} color={emissive ?? color} />
      <pointLight position={[0, -1.2, 1.5]} intensity={0.5 * light} color={color} />

      <group position={[0, -0.95, 0]}>
        <GazeRig reducedMotion={reducedMotion}>
          <Ember
            color={color}
            phase="idle"
            slideDir={null}
            facing="down"
            reducedMotion={reducedMotion}
            shape={shape}
            energy={emberEnergy}
            characterId={characterId}
            billboard={false}
            equippedProps={equippedProps}
          />
        </GazeRig>

        <mesh position={[0, 0.42, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <circleGeometry args={[0.6, 32]} />
          <meshBasicMaterial color={color} transparent opacity={0.16} />
        </mesh>
      </group>
    </Canvas>
  )
}
